import {
  fieldTitle,
  literalFieldLabels,
  readable,
  displayDescription,
  displayName,
} from "@/lib/localized-text";

import { countText, tx } from "@/lib/i18n";
import { useLocale } from "@/lib/i18n-react";
import { Fragment, useId, useState, type ReactNode } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ReferenceLine,
  XAxis,
  YAxis,
} from "recharts";
import {
  Download,
  Pencil,
  ArrowDown,
  ArrowUp,
  X,
  Maximize2,
  Settings2,
} from "lucide-react";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "./ui/chart";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { useReport } from "@/lib/query-context";
import {
  downloadCsv,
  formatValue,
  formatResultValue,
  shortDate,
} from "@/lib/format";
import type {
  QueryDefinition,
  QueryResult,
  Row,
  VisualizationConfig,
  ResultField,
} from "@/lib/types";
import { Empty, Loading, QueryError } from "./common";
import { cn } from "@/lib/utils";
import { visualizationError } from "@/lib/presentation";
import {
  CHART_COLORS as COLORS,
  mergeVisualization,
  presentationData,
} from "@/lib/dashboard-widgets";
function NestedRecords({
  value,
  fields,
}: {
  value: unknown;
  fields?: ResultField[];
}) {
  useLocale();
  const rows = (Array.isArray(value) ? value : [value]) as Row[];
  const columns = fields?.map((field) => field.name) ?? [
    ...new Set(
      rows.flatMap((row) =>
        row && typeof row === "object" ? Object.keys(row) : [],
      ),
    ),
  ];
  if (!columns.length) return <span>{JSON.stringify(value)}</span>;
  return (
    <ResultsTable
      compact
      data={{
        columns,
        rows,
        fields:
          fields ??
          columns.map((name) => ({
            name,
            label: readable(name),
            type: "unknown",
            format: "number",
          })),
        truncated: false,
        elapsedMs: 0,
        diagnostics: [],
      }}
    />
  );
}
export function ResultsTable({
  data,
  onRow,
  compact = false,
  fillHeight = false,
  fieldFormats,
  fieldLabels,
  literalLabels,
}: {
  data: QueryResult;
  onRow?: (row: Row) => void;
  compact?: boolean;
  fillHeight?: boolean;
  fieldFormats?: VisualizationConfig["fieldFormats"];
  fieldLabels?: VisualizationConfig["fieldLabels"];
  literalLabels?: Set<string>;
}) {
  useLocale();
  const literals =
    literalLabels ?? literalFieldLabels({ fieldLabels }, data.fields);
  const [page, setPage] = useState(0);
  const [expandedCells, setExpandedCells] = useState<Set<string>>(new Set());
  const tableId = useId();
  const cellKey = (rowIndex: number, column: string) =>
    `${safePage * size + rowIndex}:${column}`;
  const size = compact ? 8 : 12;
  const safePage = Math.min(
    page,
    Math.max(0, Math.ceil(data.rows.length / size) - 1),
  );
  const rows = data.rows.slice(safePage * size, (safePage + 1) * size);
  if (!data.rows.length)
    return (
      <div className={fillHeight ? "min-h-0 flex-1 overflow-auto" : undefined}>
        <Empty />
      </div>
    );
  return (
    <div className={fillHeight ? "flex h-full min-h-0 flex-col" : undefined}>
      <div
        className={cn(
          "max-w-full overflow-auto",
          fillHeight && "min-h-0 flex-1",
        )}
        role="region"
        aria-label={tx("Report rows")}
        tabIndex={0}
      >
        <Table>
          <TableHeader
            className={fillHeight ? "sticky top-0 z-10 bg-card" : undefined}
          >
            <TableRow>
              {data.columns.map((column) => (
                <TableHead
                  key={column}
                  scope="col"
                  className="whitespace-nowrap text-xs"
                >
                  {fieldTitle(
                    column,
                    data.fields,
                    fieldLabels?.[column],
                    literals.has(column),
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, i) => (
              <Fragment key={safePage * size + i}>
                <TableRow
                  className={onRow ? "cursor-pointer hover:bg-teal-50/40" : ""}
                >
                  {data.columns.map((column, ci) => (
                    <TableCell
                      key={column}
                      className="whitespace-nowrap text-xs tabular-nums"
                    >
                      {ci === 0 && onRow ? (
                        <button
                          className="font-semibold text-teal-700 underline-offset-4 hover:underline focus-visible:underline"
                          onClick={() => onRow(row)}
                        >
                          {formatValue(
                            row[column],
                            "number",
                            undefined,
                            column,
                          )}
                        </button>
                      ) : typeof row[column] === "object" &&
                        row[column] !== null ? (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 text-teal-700 hover:underline focus-visible:underline"
                          aria-expanded={expandedCells.has(cellKey(i, column))}
                          aria-controls={`${tableId}-${cellKey(i, column)}`}
                          onClick={() => {
                            const key = cellKey(i, column);
                            setExpandedCells((previous) => {
                              const next = new Set(previous);
                              if (next.has(key)) next.delete(key);
                              else next.add(key);
                              return next;
                            });
                          }}
                        >
                          <span aria-hidden="true">
                            {expandedCells.has(cellKey(i, column)) ? "▾" : "▸"}
                          </span>
                          {countText(
                            "records",
                            Array.isArray(row[column]) ? row[column].length : 1,
                          )}
                        </button>
                      ) : (
                        formatResultValue(
                          row[column],
                          data.fields.find((field) => field.name === column),
                          fieldFormats?.[column],
                        )
                      )}
                    </TableCell>
                  ))}
                </TableRow>
                {data.columns
                  .filter(
                    (column) =>
                      typeof row[column] === "object" &&
                      row[column] !== null &&
                      expandedCells.has(cellKey(i, column)),
                  )
                  .map((column) => (
                    <TableRow
                      key={column}
                      className="bg-muted/20 hover:bg-muted/20"
                    >
                      <TableCell colSpan={data.columns.length} className="p-3">
                        <div id={`${tableId}-${cellKey(i, column)}`}>
                          <div className="mb-2 text-xs font-medium text-muted-foreground">
                            {fieldTitle(
                              column,
                              data.fields,
                              fieldLabels?.[column],
                              literals.has(column),
                            )}
                          </div>
                          <NestedRecords
                            value={row[column]}
                            fields={
                              data.fields.find((field) => field.name === column)
                                ?.fields
                            }
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </div>
      <div
        className={cn(
          "flex shrink-0 flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground",
          fillHeight ? "border-t border-border px-3 py-2" : "px-1 pt-3",
        )}
      >
        <span aria-live="polite" aria-atomic="true">
          {tx("{from}–{to} of {rows}", {
            from: safePage * size + 1,
            to: Math.min((safePage + 1) * size, data.rows.length),
            rows: countText("rows", data.rows.length),
          })}
          {data.truncated ? " " + tx("· first 1,000 shown") : ""}
        </span>
        {data.rows.length > size && (
          <nav className="flex gap-1" aria-label={tx("Table pagination")}>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              disabled={safePage === 0}
              onClick={() => setPage(safePage - 1)}
            >
              {tx("Previous")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              disabled={(safePage + 1) * size >= data.rows.length}
              onClick={() => setPage(safePage + 1)}
            >
              {tx("Next")}
            </Button>
          </nav>
        )}
      </div>
    </div>
  );
}
export function Visualization({
  data,
  config,
  onRow,
  fillHeight = false,
  literalLabels,
}: {
  data: QueryResult;
  config: VisualizationConfig;
  onRow?: (row: Row) => void;
  fillHeight?: boolean;
  literalLabels?: Set<string>;
}) {
  useLocale();
  const literals = literalLabels ?? literalFieldLabels(config, data.fields);
  if (!data.rows.length) return <Empty />;
  const invalid = visualizationError(data, config);
  if (invalid)
    return (
      <Empty title={tx("Check widget configuration")} description={invalid} />
    );
  if (config.type === "table")
    return (
      <ResultsTable
        data={presentationData(data, config)}
        fillHeight={fillHeight}
        onRow={onRow}
        literalLabels={literals}
      />
    );
  const y = config.y.filter((key) => data.columns.includes(key));
  if (!y.length || (config.type !== "kpi" && !data.columns.includes(config.x)))
    return (
      <Empty
        title={tx("Choose your chart fields")}
        description={tx(
          "Select an X-axis field and numeric value from the query results, or use a table.",
        )}
      />
    );
  if (config.type === "kpi") {
    const appearance = config.kpi;
    const metricLabel = (
      <p className="text-sm leading-5 text-muted-foreground">
        {fieldTitle(
          y[0],
          data.fields,
          config.fieldLabels?.[y[0]],
          literals.has(y[0]),
        )}
      </p>
    );
    return (
      <div
        data-kpi={y[0]}
        className={cn(
          "flex min-h-20 flex-col justify-center gap-3",
          !appearance && "min-h-40 items-center",
          appearance?.alignment === "start" && "items-start text-start",
          appearance?.alignment === "center" && "items-center text-center",
          appearance?.alignment === "end" && "items-end text-end",
          fillHeight && "h-full",
        )}
      >
        {appearance?.labelPosition === "above" && metricLabel}
        <div
          data-kpi-value
          style={{ color: appearance?.valueColor }}
          className={cn(
            "max-w-full break-words font-semibold tracking-tight tabular-nums",
            !appearance || appearance.valueSize === "large"
              ? "text-5xl"
              : appearance.valueSize === "small"
                ? "text-2xl"
                : "text-3xl",
            data.rows[0][y[0]] == null && "!text-lg",
          )}
        >
          {formatValue(
            data.rows[0][y[0]],
            config.fieldFormats?.[y[0]] ?? config.format,
          )}
        </div>
        {(!appearance || appearance.labelPosition === "below") && metricLabel}
      </div>
    );
  }
  const seriesColor = (key: string, index: number) =>
    config.seriesColors?.[key] ?? COLORS[index % COLORS.length];
  const valueFormat = (key: string) =>
    config.fieldFormats?.[key] ?? config.format;
  const chartConfig: ChartConfig = Object.fromEntries(
    y.map((key, i) => [
      key,
      {
        label: fieldTitle(
          key,
          data.fields,
          config.fieldLabels?.[key],
          literals.has(key),
        ),
        color: seriesColor(key, i),
      },
    ]),
  );
  const tooltip = (
    <ChartTooltip
      content={
        <ChartTooltipContent
          labelFormatter={(value) =>
            ["date", "timestamp"].includes(
              data.fields.find((f) => f.name === config.x)?.type ?? "",
            ) && value
              ? shortDate(String(value))
              : formatValue(String(value ?? ""), "number", undefined, config.x)
          }
          nameFormatter={(name) =>
            formatValue(
              String(name ?? ""),
              "number",
              undefined,
              config.type === "pie" ? config.x : "",
            )
          }
          formatter={(value, key) => formatValue(value, valueFormat(key ?? ""))}
        />
      }
    />
  );
  if (config.type === "pie") {
    const total = data.rows.reduce(
      (sum, row) => sum + Number(row[y[0]] ?? 0),
      0,
    );
    if (!total)
      return <Empty title={tx("No recorded time in this selection")} />;
    return (
      <div
        className={cn(
          "grid items-center gap-4 sm:grid-cols-[1fr_1fr]",
          fillHeight && "min-h-full",
        )}
      >
        <div className="relative">
          <ChartContainer config={chartConfig} className="h-56 min-h-56">
            <PieChart accessibilityLayer>
              <Pie
                isAnimationActive={false}
                data={data.rows}
                dataKey={y[0]}
                nameKey={config.x}
                innerRadius={65}
                outerRadius={90}
                paddingAngle={3}
                strokeWidth={0}
              >
                {data.rows.map((_, i) => (
                  <Cell
                    key={i}
                    fill={seriesColor(String(data.rows[i]?.[config.x]), i)}
                  />
                ))}
              </Pie>
              {tooltip}
            </PieChart>
          </ChartContainer>
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-2xl font-semibold tabular-nums">
              {formatValue(total, valueFormat(y[0]))}
            </span>
            <span className="mt-1 text-[11px] text-muted-foreground">
              {fieldTitle(
                y[0],
                data.fields,
                config.fieldLabels?.[y[0]],
                literals.has(y[0]),
              )}
            </span>
          </div>
        </div>
        <div className="space-y-3 pe-3">
          {data.rows.map((row, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              <span
                className="size-2 rounded-sm"
                style={{ background: seriesColor(String(row[config.x]), i) }}
              />
              <span className="text-muted-foreground">
                {formatValue(row[config.x], "number", undefined, config.x)}
              </span>
              <span className="ms-auto font-semibold tabular-nums">
                {formatValue(row[y[0]], valueFormat(y[0]))}
              </span>
              <span className="w-9 text-end text-muted-foreground">
                {formatValue(Number(row[y[0]]) / total, "ratio")}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  const dateAxis = ["date", "timestamp"].includes(
    data.fields.find((field) => field.name === config.x)?.type ?? "",
  );
  const axis = (
    <>
      <CartesianGrid vertical={false} strokeDasharray="3 4" />
      <XAxis
        dataKey={config.x}
        axisLine={false}
        tickLine={false}
        tickMargin={12}
        minTickGap={24}
        tickFormatter={(value) =>
          dateAxis
            ? shortDate(String(value))
            : formatValue(String(value), "number", undefined, config.x)
                .replace(" Resort", "")
                .replace(" Retreat", "")
        }
      />
      <YAxis
        width={config.format === "currency" ? 96 : 64}
        axisLine={false}
        tickLine={false}
        tickMargin={8}
        tickFormatter={(value) => formatValue(Number(value), config.format)}
        domain={[
          (minimum: number) => Math.min(0, minimum),
          (maximum: number) => Math.max(0, maximum),
        ]}
      />
    </>
  );
  return (
    <div className={fillHeight ? "flex h-full min-h-0 flex-col" : undefined}>
      <ChartContainer
        config={chartConfig}
        className={fillHeight ? "min-h-60 flex-1" : "h-[240px] min-h-[240px]"}
      >
        {config.type === "line" ? (
          <AreaChart
            accessibilityLayer
            data={data.rows}
            margin={{ top: 15, right: 12, bottom: 8, left: 0 }}
          >
            {axis}
            {tooltip}
            {y.map((key, i) => (
              <Area
                isAnimationActive={false}
                key={key}
                type="monotone"
                name={fieldTitle(
                  key,
                  data.fields,
                  config.fieldLabels?.[key],
                  literals.has(key),
                )}
                dataKey={key}
                stroke={key === "target" ? "#a7b0bf" : seriesColor(key, i)}
                fill={key === "target" ? "transparent" : seriesColor(key, i)}
                fillOpacity={key === "target" ? 0 : 0.06}
                strokeWidth={key === "target" ? 1.5 : 2.5}
                strokeDasharray={key === "target" ? "5 5" : undefined}
                connectNulls={false}
              />
            ))}
            {config.target != null && (
              <ReferenceLine
                ifOverflow="extendDomain"
                y={config.target}
                stroke="#a7b0bf"
                strokeDasharray="5 5"
              />
            )}
          </AreaChart>
        ) : (
          <BarChart
            accessibilityLayer
            data={data.rows}
            margin={{ top: 15, right: 12, bottom: 8, left: 0 }}
            barGap={5}
          >
            {axis}
            {tooltip}
            {y.map((key, i) => (
              <Bar
                isAnimationActive={false}
                key={key}
                name={fieldTitle(
                  key,
                  data.fields,
                  config.fieldLabels?.[key],
                  literals.has(key),
                )}
                dataKey={key}
                fill={seriesColor(key, i)}
                radius={[4, 4, 0, 0]}
                maxBarSize={48}
                onClick={(row) => onRow?.(row as unknown as Row)}
              />
            ))}
            {config.target != null && (
              <ReferenceLine
                ifOverflow="extendDomain"
                y={config.target}
                stroke="#a7b0bf"
                strokeDasharray="5 5"
              />
            )}
          </BarChart>
        )}
      </ChartContainer>
      <div className="mt-4 flex flex-wrap justify-center gap-x-5 gap-y-2">
        {y.map((key, i) => (
          <span
            key={key}
            className="flex items-center gap-2 text-[11px] text-muted-foreground"
          >
            <span
              className="h-2 w-2 rounded-sm"
              style={{
                background: key === "target" ? "#a7b0bf" : seriesColor(key, i),
              }}
            />
            {fieldTitle(
              key,
              data.fields,
              config.fieldLabels?.[key],
              literals.has(key),
            )}
          </span>
        ))}
      </div>
    </div>
  );
}
export function ReportWidget({
  report,
  onEdit,
  onConfigure,
  onRemove,
  onMove,
  onInspect,
  editable = false,
  dragHandle,
  preview = false,
}: {
  report: QueryDefinition;
  onEdit?: (report: QueryDefinition) => void;
  onConfigure?: () => void;
  onRemove?: () => void;
  onMove?: (direction: -1 | 1) => void;
  onInspect?: (row: Row) => void;
  editable?: boolean;
  dragHandle?: ReactNode;
  preview?: boolean;
}) {
  useLocale();
  const { data, error, loading, retry } = useReport(report);
  const [fullscreen, setFullscreen] = useState(false);
  const config = mergeVisualization(report.visualization, undefined, data);
  const literals = literalFieldLabels(config, data?.fields ?? [], report);
  const appearance = config.type === "kpi" ? config.kpi : undefined;
  const invalid = data ? visualizationError(data, config) : undefined;
  const exportData = () =>
    data &&
    downloadCsv(
      presentationData(data, config),
      displayName(report),
      undefined,
      literals,
    );
  const body = (fillHeight = false) =>
    loading ? (
      <Loading />
    ) : error ? (
      <>
        <QueryError message={error} />
        <Button variant="outline" onClick={retry}>
          {tx("Retry report")}
        </Button>
      </>
    ) : data ? (
      <Visualization
        data={data}
        config={config}
        onRow={onInspect}
        fillHeight={fillHeight}
        literalLabels={literals}
      />
    ) : null;
  const actions = !preview && (
    <div
      className="flex max-w-full flex-wrap items-center justify-end gap-0.5"
      role="group"
      aria-label={tx("Actions for {p0}", { p0: displayName(report) })}
    >
      {editable && (
        <>
          {dragHandle}
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            aria-label={tx("Configure {p0}", { p0: displayName(report) })}
            onClick={onConfigure}
          >
            <Settings2 className="!size-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            aria-label={tx("Edit query for {p0}", { p0: displayName(report) })}
            onClick={() => onEdit?.(report)}
          >
            <Pencil className="!size-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            aria-label={tx("Move {p0} up", { p0: displayName(report) })}
            onClick={() => onMove?.(-1)}
          >
            <ArrowUp className="!size-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            aria-label={tx("Move {p0} down", { p0: displayName(report) })}
            onClick={() => onMove?.(1)}
          >
            <ArrowDown className="!size-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            aria-label={tx("Remove {p0}", { p0: displayName(report) })}
            onClick={onRemove}
          >
            <X className="!size-3.5" />
          </Button>
        </>
      )}
      <Button
        variant="ghost"
        size="icon"
        className="size-7"
        aria-label={tx("View {p0} full screen", { p0: displayName(report) })}
        onClick={() => setFullscreen(true)}
      >
        <Maximize2 className="!size-3.5" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="size-7"
        disabled={!data || !!invalid}
        aria-label={tx("Export {p0}", { p0: displayName(report) })}
        onClick={exportData}
      >
        <Download className="!size-3.5" />
      </Button>
    </div>
  );
  return (
    <>
      <Card
        data-widget-report={report.id}
        className="group relative flex min-w-0 flex-col overflow-hidden"
        style={
          appearance
            ? {
                backgroundColor: appearance.backgroundColor,
                borderColor: appearance.borderColor,
              }
            : undefined
        }
      >
        {config.showHeader !== false ? (
          <div className="flex flex-wrap items-start justify-between gap-3 px-5 pt-5">
            <div className="min-w-0 flex-[1_1_14rem]">
              <h3 className="text-sm font-semibold">{displayName(report)}</h3>
              {config.showDescription !== false && (
                <p className="mt-1 text-xs leading-5 text-muted-foreground">
                  {displayDescription(report)}
                </p>
              )}
            </div>
            {actions}
          </div>
        ) : (
          !preview && (
            <div
              className={cn(
                "z-10 px-2 pt-2",
                !editable &&
                  "absolute end-1 top-1 max-w-full rounded-md bg-card/95 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100",
              )}
            >
              {actions}
            </div>
          )
        )}
        <div className="min-w-0 flex-1 p-5">{body()}</div>
        {config.showFooter !== false && (
          <div className="border-t border-border bg-muted/35 px-5 py-2.5 text-[10px] text-muted-foreground">
            {tx("Saved query ·")}
            {data?.elapsedMs ?? "…"} {tx("ms")}
          </div>
        )}
      </Card>
      <Dialog open={fullscreen} onOpenChange={setFullscreen}>
        <DialogContent
          unpadded
          closeLabel={tx("Close full-screen report")}
          className="inset-0 h-dvh max-h-dvh w-full max-w-none translate-x-0 translate-y-0 rounded-none border-0 [&>div]:flex [&>div]:h-full [&>div]:min-h-0 [&>div]:flex-col"
        >
          <DialogHeader className="mb-0 shrink-0 border-b border-border p-5 pe-16">
            <DialogTitle>{displayName(report)}</DialogTitle>
            <DialogDescription>{displayDescription(report)}</DialogDescription>
          </DialogHeader>
          <div
            className="min-h-0 flex-1 overflow-auto p-5"
            style={
              appearance
                ? { backgroundColor: appearance.backgroundColor }
                : undefined
            }
          >
            {body(true)}
          </div>
          <div className="border-t p-3">
            <Button
              variant="outline"
              disabled={!data || !!invalid}
              onClick={exportData}
            >
              <Download />
              {tx("Export CSV")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
